'use client';

import React, { useState } from 'react';
import { Button } from '@/core/components/ui/button';
import { Input } from '@/core/components/ui/input';
import { toast } from '@/core/utils/toast';
import { Send, CheckSquare, Square } from 'lucide-react';
import { fetchAPI } from '@/core/api/client';
import { cn } from '@/core/utils/cn';
import { buildBackgroundGradient } from '@/core/components/Builder/blockHelpers';
import { useTranslation } from '@/core/components/providers/TranslationProvider';

interface NewsletterSettings {
    title?: string;
    subtitle?: string;
    placeholder?: string;
    buttonText?: string;
    consentText?: string;
    successMessage?: string;
    bg?: string;
    bgGradientTo?: string;
    bgPattern?: 'gradient' | 'stripes';
    bgStripeWidth?: number;
    bgStripeAngle?: number;
    textColor?: string;
    buttonBg?: string;
    buttonTextColor?: string;
    borderRadius?: number;
    layout?: 'row' | 'column';
    maxWidth?: number;
}

interface NewsletterProps {
    settings?: NewsletterSettings;
    styles?: any;
    viewport?: 'desktop' | 'tablet' | 'mobile';
}

export const Newsletter: React.FC<NewsletterProps> = ({ settings: rawSettings = {}, styles, viewport }) => {
    const { t } = useTranslation();
    // Sizing fields (borderRadius/maxWidth) are responsive — see the identical comment in
    // ContactForm.tsx for why this merge is the safe, low-risk approach here.
    const currentStyles = { ...(styles || {}), ...(viewport ? (styles || {})[viewport] || {} : {}) };
    const settings = { ...rawSettings, ...currentStyles };
    const [email, setEmail] = useState('');
    const [consentGiven, setConsentGiven] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!email.trim()) {
            toast.error(t('newsletter.error_required_email', 'Podaj adres email.'));
            return;
        }

        if (!consentGiven) {
            toast.error(t('newsletter.error_required_consent', 'Wymagane jest zaznaczenie zgody na przesyłanie wiadomości.'));
            return;
        }

        setIsSubmitting(true);

        try {
            await fetchAPI('/newsletter/subscribe', {
                method: 'POST',
                body: JSON.stringify({ email, source: 'newsletter-block', consent_given: consentGiven }),
            });
            toast.success(settings.successMessage || t('newsletter.success_default', 'Dziękujemy za zapis! Od teraz będziesz otrzymywać nasze najlepsze oferty.'));
            setEmail('');
        } catch (err: any) {
            toast.error(err?.detail || t('newsletter.error_generic', 'Nie udało się zapisać do newslettera. Spróbuj ponownie później.'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const hasCustomBg = settings.bg !== undefined;
    const isColumn = settings.layout === 'column';
    const wrapperStyle: React.CSSProperties = {
        ...(hasCustomBg ? { background: !settings.bgGradientTo ? settings.bg
            : settings.bgPattern === 'stripes'
                ? buildBackgroundGradient({ gradientType: 'stripes', gradientFrom: settings.bg, gradientTo: settings.bgGradientTo, stripeWidth: settings.bgStripeWidth, gradientAngle: settings.bgStripeAngle })
                : `linear-gradient(135deg, ${settings.bg}, ${settings.bgGradientTo})` } : {}),
        borderRadius: settings.borderRadius !== undefined ? `${settings.borderRadius}px` : undefined,
        maxWidth: settings.maxWidth !== undefined ? (settings.maxWidth === 0 ? 'none' : `${settings.maxWidth}px`) : undefined,
    };

    const defaultConsentText = t(
        'newsletter.consent_default',
        'Zgadzam się na przesyłanie informacji handlowych i marketingowych drogą elektroniczną. Wiem, że zgodę mogę wycofać w każdej chwili.'
    );

    return (
        <div
            className={cn(
                'mx-auto p-8 text-white shadow-2xl overflow-hidden relative rounded-3xl',
                settings.maxWidth === undefined && 'max-w-2xl',
                !hasCustomBg && 'bg-blue-700 shadow-blue-200'
            )}
            style={wrapperStyle}
        >
            <div className={cn('relative z-10 flex items-center gap-6', isColumn ? 'flex-col items-stretch' : 'flex-col md:flex-row')}>
                <div className={cn('flex-1 space-y-2', isColumn ? 'text-center' : 'text-center md:text-left')}>
                    <h3 className="text-2xl font-black tracking-tight" style={{ color: settings.textColor }}>
                        {settings.title || t('newsletter.title_default', 'Zostańmy w kontakcie!')}
                    </h3>
                    <p className="text-white/90 text-sm" style={{ color: settings.textColor }}>
                        {settings.subtitle || t('newsletter.subtitle_default', 'Zapisz się do newslettera i odbierz 10% rabatu na pierwsze zakupy.')}
                    </p>
                </div>

                <form
                    onSubmit={handleSubmit}
                    toolname="subscribe_newsletter"
                    tooldescription="Zapis do newslettera i odbiór dedykowanego kodu rabatowego"
                    data-webmcp-form-name="subscribe_newsletter"
                    data-webmcp-tool-name="subscribe_newsletter"
                    data-webmcp-description="Zapis do newslettera i odbiór rabatu"
                    aria-label="Formularz zapisu do newslettera"
                    className={cn('flex flex-col gap-3 w-full', !isColumn && 'md:w-auto')}
                    noValidate
                >
                    {/* Stacked on phones: side by side the button squeezed the e-mail field to a few letters. */}
                    <div className="flex flex-col sm:flex-row w-full gap-2">
                        <Input
                            type="email"
                            param-name="email"
                            param-description="Adres email subskrybenta"
                            placeholder={settings.placeholder || t('newsletter.placeholder_default', 'Twój adres email')}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className={cn('bg-white/10 border-white/20 text-white placeholder:text-blue-100 w-full min-w-0 h-12 focus-visible:ring-white/50', !isColumn && 'md:w-64')}
                        />
                        <Button
                            type="submit"
                            className={settings.buttonBg ? 'h-12 px-6 font-bold shrink-0' : 'bg-white text-blue-700 hover:bg-white/90 h-12 px-6 font-bold shrink-0'}
                            style={settings.buttonBg ? { backgroundColor: settings.buttonBg, color: settings.buttonTextColor ?? '#2563eb' } : undefined}
                            disabled={isSubmitting}
                        >
                            <Send className="w-4 h-4 mr-2" />
                            {isSubmitting ? '...' : (settings.buttonText || t('newsletter.submit_default', 'Zapisz się'))}
                        </Button>
                    </div>

                    <label className="flex items-start gap-2.5 cursor-pointer text-xs text-white leading-snug select-none mt-1" style={{ color: settings.textColor }}>
                        <input
                            type="checkbox"
                            checked={consentGiven}
                            onChange={(e) => setConsentGiven(e.target.checked)}
                            className="sr-only"
                        />
                        <span className="mt-0.5 shrink-0 text-white">
                            {consentGiven ? <CheckSquare className="w-4 h-4 text-white" /> : <Square className="w-4 h-4 text-white/60" />}
                        </span>
                        <span>{settings.consentText || defaultConsentText}</span>
                    </label>
                </form>
            </div>

            {!hasCustomBg && (
                <>
                    <div className="absolute top-0 right-0 -translate-y-12 translate-x-12 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
                    <div className="absolute bottom-0 left-0 translate-y-12 -translate-x-12 w-32 h-32 bg-blue-400/30 rounded-full blur-2xl" />
                </>
            )}
        </div>
    );
};
